// #OnePlanTaxonomy — Picker de objetos del mapa con árbol jerárquico
// colapsable + buscador. Replica el panel "Add Objects" de OnePlan
// (~200 items en 19 categorías top-level) en español.
//
// Uso:
//   <MapObjectPicker selectedId={categoryId} onSelect={(item) => ...} />
//
// El callback recibe el objeto completo: { id, label, color, icon, type, parentLabel }.
// Filtra solo type='point' por default (la fase actual no soporta polygon/polyline).
//
import React, { useMemo, useState } from 'react';
import { Search, ChevronRight, ChevronDown, X } from 'lucide-react';
import { MAP_OBJECT_TREE } from '../lib/mapObjectCategories';

// Helper: aplana el árbol respetando jerarquía. Devuelve filas listas
// para renderizar como lista plana cuando se filtra por búsqueda.
function flattenForSearch(tree) {
  const rows = [];
  const walk = (nodes, breadcrumb = []) => {
    for (const node of nodes) {
      if (node.children) {
        walk(node.children, [...breadcrumb, node.label]);
      } else {
        rows.push({ ...node, breadcrumb });
      }
    }
  };
  walk(tree);
  return rows;
}

function CategoryNode({ node, depth, selectedId, onSelect, expandedSet, toggleExpanded, allowedTypes }) {
  const isExpanded = expandedSet.has(node.id);
  const hasChildren = !!node.children?.length;

  if (hasChildren) {
    // Solo render si tiene al menos un descendant del tipo permitido
    const hasMatchingDescendant = (n) => {
      if (n.children) return n.children.some(hasMatchingDescendant);
      const t = n.type || 'point';
      return !allowedTypes || allowedTypes.includes(t);
    };
    if (!hasMatchingDescendant(node)) return null;

    return (
      <div>
        <button
          type="button"
          onClick={() => toggleExpanded(node.id)}
          className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded-sm"
          style={{ paddingLeft: 8 + depth * 12 }}
        >
          {isExpanded ? <ChevronDown size={13} className="text-slate-400" /> : <ChevronRight size={13} className="text-slate-400" />}
          {node.icon && <span style={{ fontSize: 14 }}>{node.icon}</span>}
          <span className="font-semibold text-slate-700 truncate">{node.label}</span>
        </button>
        {isExpanded && (
          <div>
            {node.children.map((child) => (
              <CategoryNode
                key={child.id}
                node={{ ...child, type: child.type || node.type || 'point' }}
                depth={depth + 1}
                selectedId={selectedId}
                onSelect={onSelect}
                expandedSet={expandedSet}
                toggleExpanded={toggleExpanded}
                allowedTypes={allowedTypes}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Leaf node (item)
  const nodeType = node.type || 'point';
  if (allowedTypes && !allowedTypes.includes(nodeType)) {
    // Render disabled with "Próximamente" tooltip
    return (
      <div
        className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-not-allowed opacity-40"
        style={{ paddingLeft: 8 + depth * 12 + 13 }}
        title={`${nodeType === 'polygon' ? 'Polígono' : 'Línea'} — disponible próximamente`}
      >
        <span
          className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: node.color || '#94A3B8' }}
        />
        {node.icon && <span style={{ fontSize: 14 }}>{node.icon}</span>}
        <span className="truncate text-slate-500">{node.label}</span>
        <span className="ml-auto text-[9px] uppercase text-slate-400 font-bold">próx.</span>
      </div>
    );
  }

  const isSelected = selectedId === node.id;
  return (
    <button
      type="button"
      onClick={() => onSelect(node)}
      className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm transition-colors ${
        isSelected ? 'bg-soem-primary text-white' : 'hover:bg-slate-100 text-slate-700'
      }`}
      style={{ paddingLeft: 8 + depth * 12 + 13 }}
    >
      <span
        className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: node.color || '#94A3B8' }}
      />
      {node.icon && <span style={{ fontSize: 14 }}>{node.icon}</span>}
      <span className="truncate">{node.label}</span>
    </button>
  );
}

export default function MapObjectPicker({
  selectedId,
  onSelect,
  allowedTypes = ['point'], // por ahora solo point; futuras fases: ['point','polyline','polygon']
  maxHeight = 380,
}) {
  const [search, setSearch] = useState('');
  // Por default, todas las categorías top-level y sus inmediatos están colapsados
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const ids = new Set();
    const walk = (nodes) => {
      for (const n of nodes) {
        if (n.children) {
          ids.add(n.id);
          walk(n.children);
        }
      }
    };
    walk(MAP_OBJECT_TREE);
    setExpanded(ids);
  };
  const collapseAll = () => setExpanded(new Set());

  // Resultados de búsqueda (lista plana con breadcrumb)
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const flat = flattenForSearch(MAP_OBJECT_TREE);
    return flat.filter((item) => {
      const matches =
        item.label.toLowerCase().includes(q) ||
        item.breadcrumb.some((b) => b.toLowerCase().includes(q));
      if (!matches) return false;
      const t = item.type || 'point';
      return !allowedTypes || allowedTypes.includes(t);
    }).slice(0, 100); // cap a 100 resultados para perf
  }, [search, allowedTypes]);

  // Si maxHeight='100%' (caso sidebar Fase E) usamos height:100% + h-full
  // para que el picker llene el contenedor padre. Si es number (caso modal),
  // aplicamos maxHeight como antes.
  const isFill = maxHeight === '100%';
  const wrapperStyle = isFill ? { height: '100%' } : { maxHeight };
  return (
    <div
      className={`border border-slate-200 rounded-sm bg-white flex flex-col ${isFill ? 'h-full' : ''}`}
      style={wrapperStyle}
    >
      {/* Search bar */}
      <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar objeto..."
            className="w-full pl-7 pr-7 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-soem-primary"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={12} />
            </button>
          )}
        </div>
        {!search && (
          <div className="flex gap-2 mt-1.5">
            <button type="button" onClick={expandAll} className="text-[10px] text-soem-primary hover:underline">Expandir todo</button>
            <span className="text-[10px] text-slate-300">·</span>
            <button type="button" onClick={collapseAll} className="text-[10px] text-soem-primary hover:underline">Colapsar todo</button>
          </div>
        )}
      </div>

      {/* Tree / Search results */}
      <div className="overflow-y-auto flex-1 p-1">
        {search ? (
          searchResults && searchResults.length > 0 ? (
            searchResults.map((item) => {
              const isSelected = selectedId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(item)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm transition-colors ${
                    isSelected ? 'bg-soem-primary text-white' : 'hover:bg-slate-100'
                  }`}
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color || '#94A3B8' }}
                  />
                  {item.icon && <span style={{ fontSize: 14 }}>{item.icon}</span>}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="truncate">{item.label}</div>
                    {item.breadcrumb?.length > 0 && (
                      <div className={`text-[10px] truncate ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                        {item.breadcrumb.join(' › ')}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          ) : (
            <p className="text-xs text-slate-400 text-center py-4">Sin resultados para "{search}"</p>
          )
        ) : (
          MAP_OBJECT_TREE.map((node) => (
            <CategoryNode
              key={node.id}
              node={node}
              depth={0}
              selectedId={selectedId}
              onSelect={onSelect}
              expandedSet={expanded}
              toggleExpanded={toggleExpanded}
              allowedTypes={allowedTypes}
            />
          ))
        )}
      </div>
    </div>
  );
}
